import { useCallback, useEffect, useRef, useState } from "react";
import { BadgeCheck } from "lucide-react";
import type { AppLocale, Translate } from "../i18n";
import {
  applyCoupon,
  CheckoutApiError,
  DOODLES_PRICE_PENDING,
  getOrderSummary,
  isDoodlesPricePending,
  parseDoodlesPricing,
  removeCoupon,
  startCheckout,
  type DoodlesPricing,
  type OrderSummary
} from "../lib/api";
import { BACKGROUND_POLL_DELAY_MS, PENDING_STILL_WORKING_AFTER_MS, retryWhilePending } from "../lib/pending-retry";
import { formatMoney } from "../lib/money";
import { formatAddressLines } from "../lib/address";
import { BRAND } from "../lib/brand";
import { rememberTokenForOrder } from "../lib/session-token";
import { rememberThemeForOrder, themeFromSearch } from "../lib/theme-lock";
import { Redirecting } from "./Redirecting";
import { CountryClosed, isProviderCountryClosed, useFailureReturnUrl } from "./CountryClosed";
import { flowCopy } from "./flow-copy";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; code: string; message: string; providerClosed?: boolean }
  | { kind: "paid"; summary: OrderSummary }
  | { kind: "ready"; summary: OrderSummary }
  // A multicolor Doodles price is still being calculated from the figurine's
  // own slice (409 `doodles_price_pending`, ADR-DSP1). No payment control is
  // rendered in this state; `pricing` (when known) only supplies the floor.
  | { kind: "pricePending"; pricing: DoodlesPricing | null; stillWorking: boolean };

/** A summary that still carries a pending lock is never payable: treat it like the 409. */
function pricePendingLowerBound(pricing: DoodlesPricing, summary: OrderSummary | null): number {
  const figurine = pricing.sizeBaseMinor + pricing.multicolorFloorMinor;
  if (!summary || summary.price.currency !== pricing.currency) return figurine;
  const { premiumServiceMinor = 0, shippingMinor, discountMinor = 0 } = summary.price;
  return Math.max(0, figurine + premiumServiceMinor + shippingMinor - discountMinor);
}

async function loadPayableSummary(token: string): Promise<OrderSummary> {
  const summary = await getOrderSummary(token);
  if (summary.paymentStatus !== "paid" && summary.pricing?.status === "pending_estimate") {
    throw new CheckoutApiError(409, DOODLES_PRICE_PENDING, "Price pending", null, { pricing: summary.pricing });
  }
  return summary;
}

export function ReviewAndPay({
  t,
  locale,
  token,
  navigate
}: {
  t: Translate;
  locale: AppLocale;
  token: string;
  navigate: (path: string) => void;
}) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [consent, setConsent] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [couponOpen, setCouponOpen] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponBusy, setCouponBusy] = useState<"apply" | "remove" | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  // Tracks the authoritative-summary refetch that follows every coupon
  // apply/remove. "pending" = mutation succeeded, refetch in flight (the
  // price card keeps showing the last-known numbers, de-emphasized, never the
  // mutation response's own totals). "error" = the refetch itself failed, so
  // the displayed numbers are stale and unconfirmed; Pay stays disabled until
  // a retry succeeds.
  const [priceRefresh, setPriceRefresh] = useState<"idle" | "pending" | "error">("idle");
  // Bumped to start a fresh bounded wait for a pending multicolor price (a
  // manual "try again", or a checkout-session 409 at Pay time).
  const [loadRun, setLoadRun] = useState(0);
  const lastPricing = useRef<DoodlesPricing | null>(null);
  // The last payable summary this screen showed. Its non-figurine lines
  // (premium service, shipping, applied coupon) carry over to the "Starting
  // from" bound, so it is composed exactly like the final total.
  const lastSummary = useRef<OrderSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    let waitTimer: ReturnType<typeof setTimeout> | undefined;
    let stillWorkingTimer: ReturnType<typeof setTimeout> | undefined;
    // Unmount settles the pending wait so the loop ends instead of awaiting a
    // cleared timer forever.
    let wakeWait: (() => void) | undefined;
    const wait = (delayMs: number) =>
      new Promise<void>((resolve) => {
        wakeWait = resolve;
        waitTimer = setTimeout(() => {
          wakeWait = undefined;
          resolve();
        }, delayMs);
      });
    const startStillWorkingTimer = () => {
      if (stillWorkingTimer !== undefined) return;
      stillWorkingTimer = setTimeout(() => {
        if (cancelled) return;
        setState((prev) => (prev.kind === "pricePending" ? { ...prev, stillWorking: true } : prev));
      }, PENDING_STILL_WORKING_AFTER_MS);
    };
    setState((prev) =>
      prev.kind === "pricePending" ? { ...prev, stillWorking: false } : { kind: "loading" }
    );
    if (loadRun > 0) startStillWorkingTimer();
    const options = {
      isPending: isDoodlesPricePending,
      isCancelled: () => cancelled,
      wait,
      onPending: (error: unknown) => {
        const fromError = error instanceof CheckoutApiError ? parseDoodlesPricing(error.details?.pricing) : null;
        if (fromError) lastPricing.current = fromError;
        const pricing = lastPricing.current;
        setState((prev) =>
          prev.kind === "pricePending"
            ? { ...prev, pricing: pricing ?? prev.pricing }
            : { kind: "pricePending", pricing, stillWorking: false }
        );
        startStillWorkingTimer();
      }
    };
    const load = () => loadPayableSummary(token);
    // Same as the Handoff: the bounded run first, then a read every 15 s with
    // no give-up state. Only the server settling the price ends the wait.
    void (async () => {
      let result = await retryWhilePending(load, options);
      while (result.kind === "exhausted") {
        result = await retryWhilePending(load, { ...options, delays: [BACKGROUND_POLL_DELAY_MS] });
      }
      return result;
    })()
      .then((result) => {
        if (cancelled || result.kind === "cancelled") return;
        const summary = result.value;
        lastSummary.current = summary;
        if (summary.pricing) lastPricing.current = summary.pricing;
        // Keep the capability in this tab after the authenticated summary
        // resolves. Success and Cancelled use it to fetch the current registry
        // return from /api on every load; return URLs themselves are never read
        // from the query string or browser storage.
        rememberTokenForOrder(summary.orderId, token);
        // The merchant's theme lock rides the same round-trip: the Stripe
        // success/cancel URLs carry no query, so the confirmation screens
        // recall it from this stash instead of falling back to light.
        const lockedTheme = themeFromSearch(window.location.search);
        if (lockedTheme) rememberThemeForOrder(summary.orderId, lockedTheme);
        setState(summary.paymentStatus === "paid" ? { kind: "paid", summary } : { kind: "ready", summary });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const code = error instanceof CheckoutApiError ? error.code : "unknown_error";
        setState({ kind: "error", code, message: errorMessage(t, error), providerClosed: isProviderCountryClosed(error) });
      });
    return () => {
      cancelled = true;
      if (waitTimer !== undefined) clearTimeout(waitTimer);
      wakeWait?.();
      wakeWait = undefined;
      if (stillWorkingTimer !== undefined) clearTimeout(stillWorkingTimer);
    };
  }, [token, t, loadRun]);

  const onPay = useCallback(async () => {
    if (!consent) return;
    setPayError(null);
    setRedirecting(true);
    try {
      const session = await startCheckout(token);
      // Refresh the tab-scoped capability at the Stripe handoff as well.
      rememberTokenForOrder(session.orderId, token);
      // Hand off to the Stripe-hosted Checkout page.
      window.location.assign(session.url);
    } catch (error: unknown) {
      setRedirecting(false);
      if (isDoodlesPricePending(error)) {
        // The server refused to open a payment while the multicolor price is
        // still being calculated: hide Pay and wait for the final price.
        lastPricing.current = parseDoodlesPricing(error.details?.pricing) ?? lastPricing.current;
        setState({ kind: "pricePending", pricing: lastPricing.current, stillWorking: false });
        setLoadRun((run) => run + 1);
      } else if (error instanceof CheckoutApiError && error.code === "country_closed") {
        setState({ kind: "error", code: error.code, message: errorMessage(t, error), providerClosed: isProviderCountryClosed(error) });
      } else setPayError(errorMessage(t, error));
    }
  }, [consent, token, t]);

  // Re-fetch the summary (the ONLY authoritative source for displayed totals)
  // after a coupon apply/remove. Returns whether it succeeded so callers can
  // drive the price card's pending/error state; never writes a mutation
  // response's own numbers into the display.
  const refreshSummary = useCallback(async (): Promise<boolean> => {
    try {
      const fresh = await loadPayableSummary(token);
      lastSummary.current = fresh;
      setState((prev) =>
        prev.kind === "ready" || prev.kind === "paid"
          ? { kind: fresh.paymentStatus === "paid" ? "paid" : "ready", summary: fresh }
          : prev
      );
      return true;
    } catch (error: unknown) {
      if (isDoodlesPricePending(error)) {
        lastPricing.current = parseDoodlesPricing(error.details?.pricing) ?? lastPricing.current;
        setState({ kind: "pricePending", pricing: lastPricing.current, stillWorking: false });
        setLoadRun((run) => run + 1);
        return true;
      }
      if (error instanceof CheckoutApiError && error.code === "country_closed") {
        setState({ kind: "error", code: error.code, message: errorMessage(t, error), providerClosed: isProviderCountryClosed(error) });
      }
      return false;
    }
  }, [token, t]);

  const onRetryPriceRefresh = useCallback(async () => {
    setPriceRefresh("pending");
    const ok = await refreshSummary();
    setPriceRefresh(ok ? "idle" : "error");
  }, [refreshSummary]);

  const onApplyCoupon = useCallback(async () => {
    const code = couponInput.trim();
    if (!code) return;
    setCouponBusy("apply");
    setCouponError(null);
    try {
      // The apply response (couponCode/discountMinor/totalAfterDiscountMinor)
      // is intentionally never written to the displayed summary: it's in the
      // quote currency, its total omits charge-time tax, and the existing
      // summary may be in a different (cross-currency, pay-in) currency —
      // mixing the two would show a total that doesn't match the charge. Only
      // a fresh getOrderSummary() result is ever rendered.
      await applyCoupon(token, code);
      setCouponOpen(false);
      setCouponInput("");
      setPriceRefresh("pending");
      const ok = await refreshSummary();
      setPriceRefresh(ok ? "idle" : "error");
    } catch (error: unknown) {
      if (error instanceof CheckoutApiError && error.code === "country_closed") {
        setState({ kind: "error", code: error.code, message: errorMessage(t, error), providerClosed: isProviderCountryClosed(error) });
      } else setCouponError(couponErrorMessage(t, error));
    } finally {
      setCouponBusy(null);
    }
  }, [couponInput, token, refreshSummary, t]);

  const onRemoveCoupon = useCallback(async () => {
    setCouponBusy("remove");
    setCouponError(null);
    try {
      await removeCoupon(token);
      setPriceRefresh("pending");
      const ok = await refreshSummary();
      setPriceRefresh(ok ? "idle" : "error");
    } catch (error: unknown) {
      if (error instanceof CheckoutApiError && error.code === "country_closed") {
        setState({ kind: "error", code: error.code, message: errorMessage(t, error), providerClosed: isProviderCountryClosed(error) });
      } else setCouponError(couponErrorMessage(t, error));
    } finally {
      setCouponBusy(null);
    }
  }, [token, refreshSummary, t]);

  // A failed checkout (closed country, expired or unknown link) still returns
  // the customer to the storefront that created the order (e.g. Doodles), not
  // the Tinkr marketing site, whenever /api can name that registry return.
  const failureReturnUrl = useFailureReturnUrl(token, state.kind === "error", lastSummary.current?.returnUrl);

  if (redirecting) return <Redirecting t={t} />;

  if (state.kind === "loading") {
    return (
      <main className="co-state">
        <div className="co-spinner" aria-hidden />
        <p>{t("common.loading")}…</p>
      </main>
    );
  }

  if (state.kind === "pricePending") {
    const { pricing, stillWorking } = state;
    // Floor-based lower bound, composed like the final total: size base plus
    // the minimum multicolor surcharge, plus the summary's premium service and
    // shipping lines, minus its applied coupon. Without a same-currency
    // summary it falls back to size base + floor.
    const lowerBound = pricing ? pricePendingLowerBound(pricing, lastSummary.current) : null;
    return (
      <main className="co-center co-price-pending">
        <div className="co-spinner" aria-hidden />
        <h1>{t("review.pricing.calculating")}</h1>
        <p>{t("review.pricing.calculatingBody")}</p>
        {pricing && lowerBound !== null && (
          <section className="co-card" style={{ width: "100%" }}>
            <div className="co-price-row">
              <span>{t("review.pricing.from")}</span>
              <span>{formatMoney(lowerBound, pricing.currency, locale)}</span>
            </div>
          </section>
        )}
        <p role="status" aria-live="polite">
          {stillWorking ? t("review.pricing.stillWorking") : ""}
        </p>
      </main>
    );
  }

  if (state.kind === "error" && state.code === "country_closed") {
    return <CountryClosed locale={locale} providerClosed={state.providerClosed === true} returnUrl={failureReturnUrl} />;
  }

  if (state.kind === "error") {
    // Expired / revoked / not-found link. Never a dead end (mirrors the
    // Cancelled screen's pattern): the emailed order link always re-mints a
    // fresh checkout link, so point the customer there, at the site, and at a
    // human. There is no reopen-from-here path — the URL token IS the broken
    // credential, and this branch has no order id to recall a stored one by.
    return (
      <main className="co-state">
        <p>{state.code === "order_access_reverification_required" ? flowCopy(locale).recoverIntro : state.message}</p>
        <p>{t("error.recoveryHint")}</p>
        <div className="co-stack">
          {(state.code === "order_access_expired" || state.code === "order_access_reverification_required") && <button type="button" className="co-cta"
            onClick={() => navigate(`/o/${token}/recover`)}>{flowCopy(locale).recoverSend}</button>}
          <button
            type="button"
            className="co-cta"
            onClick={() => window.location.assign(failureReturnUrl ?? BRAND.marketingUrl)}
          >
            {t("error.backToSite")}
          </button>
          <a className="co-btn-secondary" href={`mailto:${BRAND.supportEmail}`}>
            {t("cancelled.help")}
          </a>
        </div>
      </main>
    );
  }

  if (state.kind === "paid") {
    // Already paid — send the customer to the success view rather than re-charging.
    return (
      <main className="co-state">
        <BadgeCheck size={40} aria-hidden style={{ color: "var(--brand-deep)" }} />
        <p>{t("error.alreadyPaid")}</p>
        <button
          type="button"
          className="co-btn-secondary"
          onClick={() => navigate(`/orders/${state.summary.orderId}/paid`)}
        >
          {t("success.track")}
        </button>
        {state.summary.partnerOrder && <button type="button" className="co-btn-secondary" onClick={() => navigate(`/o/${token}/status`)}>{t("success.viewOrder")}</button>}
        <button type="button" className="co-btn-secondary" onClick={() => navigate(`/o/${token}/privacy`)}>{flowCopy(locale).privacyLink}</button>
      </main>
    );
  }

  const { summary } = state;
  // The price block is already in the CUSTOMER's currency (the API converts the
  // whole block to the buyer's pay-in currency on a cross-currency order), so the
  // breakdown + the Pay CTA all read straight from `price` — one currency.
  const { price } = summary;
  // True for the whole mutation + refetch window (apply/remove in flight, the
  // follow-up summary refetch in flight, or that refetch having failed and
  // not yet been retried successfully). Gates Pay and every coupon control so
  // the customer can never act on a total that isn't confirmed-fresh.
  const priceBusy = couponBusy !== null || priceRefresh !== "idle";
  const multicolor = multicolorLine(summary);
  const specs = [
    summary.item.material,
    summary.item.color ? `(${summary.item.color})` : null
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <main className="co-page co-review">
      <div className="co-review-main">
        {/* Trust badge: Sold by Tinkr · Produced by an approved local partner */}
        <div className="co-trust">
          <span className="co-diamond" aria-hidden />
          <span>{t("trust.badge")}</span>
        </div>

        {/* Order summary */}
        <section className="co-card">
          <h2 className="co-card-title">{t("review.orderSummary")}</h2>
          <div className="co-summary">
            {/* The item picture when the summary carries one (a Doodles order
                signs the child's own drawing); otherwise the brand-placeholder
                tile — the Tinkr mark reads as deliberate where stacked
                diamonds would read as a broken thumbnail. A failed image load
                (e.g. the short-lived signed URL expired in a long-idle tab)
                falls back to the placeholder too. */}
            <div className="co-thumb" aria-hidden>
              {summary.item.imageUrl && !thumbFailed ? (
                <img
                  className="co-thumb-img"
                  src={summary.item.imageUrl}
                  alt=""
                  onError={() => setThumbFailed(true)}
                />
              ) : (
                <img className="co-thumb-logo" src={BRAND.logos.fullColorMark} alt="" />
              )}
              {/* After the image in the DOM so the absolutely-positioned pill
                  paints above it. */}
              <span className="co-thumb-pill">{t("review.threeD")}</span>
            </div>
            <div className="co-summary-body">
              <p className="co-summary-title">{summary.item.title}</p>
              <p className="co-summary-specs">
                {specs} · {t("review.specsQty")}{t("common.labelSeparator")}{new Intl.NumberFormat(locale).format(summary.item.quantity)}
              </p>
              <p className="co-summary-row">
                <span className="co-diamond is-small" aria-hidden />
                {deliveryLabel(t, summary.delivery.serviceLevel)}
              </p>
            </div>
          </div>
        </section>

        {/* Shipping details */}
        {summary.shippingAddress && (
          <section className="co-card">
            <h2 className="co-card-title">{t("review.shipping.title")}</h2>
            <div className="co-address">
              {formatAddressLines(summary.shippingAddress, locale).map((line, i) => (
                <div key={i} className={i === nameLineIndex(summary) ? "co-address-name" : undefined}>
                  {line}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Payment method: Card only. The USDC/stablecoin rail is intentionally
            not shown in checkout yet — it only appears once an admin enables it
            (a later change wires that flag). The unused `review.payment.usdc` /
            `review.payment.comingSoon` strings + `.co-soon-badge` styles are
            kept so re-enabling is a one-line JSX restore. */}
        <section className="co-card">
          <h2 className="co-card-title">{t("review.payment.title")}</h2>
          {/* Design v3 payment rows: radio + copy only (no method icons). */}
          <div className="co-pay-option" data-selected="true">
            <span className="co-pay-radio">
              <span />
            </span>
            <span className="co-pay-text">
              <span className="co-pay-name">{t("review.payment.card")}</span>
              <span className="co-pay-hint">{t("review.payment.cardHint")}</span>
            </span>
          </div>
        </section>
      </div>

      {/* Right rail on desktop (design C3: price → consent → Pay stays in
          view); flows inline on mobile where the action bar re-fixes to the
          bottom edge (design C1). */}
      <aside className="co-rail">
        {/* Price: GROUPED public line items only — never a fee/payout teardown */}
        <section className="co-card">
          <h2 className="co-card-title">{t("review.price.title")}</h2>
          {/* These numbers are always the last CONFIRMED getOrderSummary()
              result — never a coupon apply/remove response's own totals. While
              a refetch is in flight or has failed, the whole block below is
              de-emphasized (`is-pending`) rather than replaced, so the
              customer never sees a total that mixes bases. */}
          <div className={`co-price-lines${priceRefresh !== "idle" ? " is-pending" : ""}`}>
            <div className="co-price-row">
              <span>{t("review.price.itemSubtotal")}</span>
              <span>{formatMoney(multicolor ? multicolor.baseMinor : price.itemSubtotalMinor, price.currency, locale)}</span>
            </div>
            {multicolor && (
              <div className="co-price-row co-multicolor-row">
                <span>{t("review.price.multicolor")}</span>
                <span>+{formatMoney(multicolor.surchargeMinor, price.currency, locale)}</span>
              </div>
            )}
            {typeof price.premiumServiceMinor === "number" && price.premiumServiceMinor > 0 && (
              <div className="co-price-row co-premium-row">
                <span>{t("review.price.premium")}</span>
                <span>{formatMoney(price.premiumServiceMinor, price.currency, locale)}</span>
              </div>
            )}
            <div className="co-price-row">
              <span>{t("review.price.shipping")}</span>
              <span>{price.shippingMinor === 0 ? t("review.price.shippingIncluded") : formatMoney(price.shippingMinor, price.currency, locale)}</span>
            </div>
            {price.couponCode && typeof price.discountMinor === "number" && (
              <div className="co-price-row co-coupon-discount-row">
                <span>
                  {t("coupon.appliedLabel")} ({price.couponCode})
                </span>
                <span className="co-coupon-discount-amount">
                  -{formatMoney(price.discountMinor, price.currency, locale)}
                </span>
              </div>
            )}
            <div className="co-price-row co-price-total">
              <span>{t("review.price.total")}</span>
              <span className="co-price-amount">{formatMoney(price.totalMinor, price.currency, locale)}</span>
            </div>
          </div>
          {summary.pricing?.mode === "slice" && summary.pricing.status === "fallback_cap" && (
            <p className="co-price-note co-multicolor-cap-note">{t("review.price.multicolorCapNote")}</p>
          )}
          <p className="co-price-note">{t("review.price.taxNote")}</p>
          {priceRefresh === "pending" && (
            <p className="co-price-refresh-status" aria-live="polite">
              {t("review.price.updating")}
            </p>
          )}
          {priceRefresh === "error" && (
            <p className="co-price-refresh-status co-price-refresh-error" aria-live="polite">
              {t("review.price.refreshError")}{" "}
              <button type="button" className="co-price-refresh-retry" onClick={onRetryPriceRefresh}>
                {t("common.retry")}
              </button>
            </p>
          )}

          {/* Coupon entry. Hidden entirely once paid (the whole ReviewAndPay
              screen already short-circuits to the "paid" branch above before
              this JSX is ever reached, so no extra paymentStatus check is
              needed here). */}
          <div className="co-coupon">
            {price.couponCode ? (
              <div className="co-coupon-applied">
                <span className="co-coupon-applied-text">
                  {t("coupon.appliedLabel")}{t("common.labelSeparator")}<strong>{price.couponCode}</strong>
                </span>
                <button
                  type="button"
                  className="co-coupon-remove"
                  onClick={onRemoveCoupon}
                  disabled={priceBusy}
                >
                  {couponBusy === "remove" ? t("coupon.removing") : t("coupon.remove")}
                </button>
              </div>
            ) : couponOpen ? (
              <form
                className="co-coupon-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  onApplyCoupon();
                }}
              >
                <label className="co-coupon-input-label" htmlFor="co-coupon-code">
                  {t("coupon.label")}
                </label>
                <div className="co-coupon-input-row">
                  <input
                    id="co-coupon-code"
                    type="text"
                    className="co-coupon-input"
                    value={couponInput}
                    maxLength={32}
                    autoCapitalize="characters"
                    autoComplete="off"
                    placeholder={t("coupon.placeholder")}
                    disabled={priceBusy}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  />
                  <button
                    type="submit"
                    className="co-coupon-apply"
                    disabled={priceBusy || couponInput.trim().length === 0}
                  >
                    {couponBusy === "apply" ? t("coupon.applying") : t("coupon.apply")}
                  </button>
                </div>
                <p className="co-coupon-error" aria-live="polite">
                  {couponError ?? ""}
                </p>
              </form>
            ) : (
              <button
                type="button"
                className="co-coupon-toggle"
                onClick={() => setCouponOpen(true)}
                disabled={priceBusy}
              >
                {t("coupon.toggle")}
              </button>
            )}
          </div>
        </section>

        {/* Consent: Pay is disabled until checked */}
        <button type="button" className="co-btn-secondary" onClick={() => navigate(`/o/${token}/privacy`)}>{flowCopy(locale).privacyLink}</button>
        <label className="co-consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span>
            {t("review.consent.prefix")}{" "}
            <a
              href="/legal/terms"
              onClick={(e) => {
                e.preventDefault();
                navigate("/legal/terms");
              }}
            >
              {t("review.consent.terms")}
            </a>{" "}
            {t("review.consent.and")}{" "}
            <a
              href="/legal/privacy"
              onClick={(e) => {
                e.preventDefault();
                navigate("/legal/privacy");
              }}
            >
              {t("review.consent.privacy")}
            </a>
            .
          </span>
        </label>

        {payError && <p className="co-price-note" style={{ color: "var(--brand-strong)" }}>{payError}</p>}

        {/* Fixed bottom sheet on mobile; static panel row on desktop. */}
        <div className="co-actionbar">
          <div className="co-actionbar-inner">
            <button type="button" className="co-cta" disabled={!consent || priceBusy} onClick={onPay}>
              {t("review.cta.pay")} {formatMoney(price.totalMinor, price.currency, locale)}
            </button>
            <p className="co-cta-secure">
              <span className="co-diamond is-small" aria-hidden /> {t("review.cta.secure")}
            </p>
          </div>
        </div>
      </aside>
    </main>
  );
}

/**
 * The "Multicolor +X" split under the base, for a slice-priced Doodles order
 * whose price is settled (final or capped). Rendered only when it reconciles
 * exactly with the item subtotal in the same currency, so the visible lines
 * always add up; otherwise the single item-subtotal line stays.
 */
function multicolorLine(summary: OrderSummary): { baseMinor: number; surchargeMinor: number } | null {
  const pricing = summary.pricing;
  if (!pricing || pricing.mode !== "slice" || pricing.status === "pending_estimate") return null;
  const surchargeMinor = pricing.multicolorSurchargeMinor;
  if (surchargeMinor === null || surchargeMinor <= 0) return null;
  if (pricing.currency !== summary.price.currency) return null;
  if (pricing.sizeBaseMinor + surchargeMinor !== summary.price.itemSubtotalMinor) return null;
  return { baseMinor: pricing.sizeBaseMinor, surchargeMinor };
}

function nameLineIndex(summary: OrderSummary): number {
  // The recipient-name line gets bolded. Western addresses lead with the name
  // (index 0); JP places the 様-honored name after postal + address (+ optional
  // building line).
  const addr = summary.shippingAddress;
  if (!addr) return -1;
  if (!("prefecture" in addr)) return 0;
  return addr.building ? 3 : 2;
}

function deliveryLabel(t: Translate, serviceLevel: string): string {
  // The locked quote carries the service level, not a literal date window; render
  // the level in the delivery row (a date window would come from tracking later).
  void serviceLevel;
  return t("review.delivery.title");
}

function errorMessage(t: Translate, error: unknown): string {
  if (error instanceof CheckoutApiError) {
    if (error.code === "country_closed") {
      return t(isProviderCountryClosed(error) ? "error.providerCountryClosed" : "error.countryClosed");
    }
    if (error.code === "order_access_expired" || error.code === "quote_expired") {
      return t("error.expired");
    }
    if (error.status === 404 || error.code === "order_not_found") return t("error.notFound");
    if (error.code === "order_access_revoked") return t("error.notFound");
    // Thrown at checkout-session creation when an already-applied coupon has
    // since been revoked/expired/hit its usage limit server-side; the fix is
    // for the customer to remove it (Remove button in the price card) and
    // retry, not a generic "try again".
    if (error.code === "coupon_no_longer_valid") return t("error.couponNoLongerValid");
    // Other orders hold the coupon's remaining slots right now (ADR-P16 §7); a
    // slot can free up, so the customer may remove it or try again later.
    if (error.code === "coupon_usage_limit_reached") return t("error.couponLimitReached");
    // The applied coupon does not fit this total: same fix, remove it.
    if (error.code === "coupon_not_applicable_to_total") return t("error.couponNotApplicable");
    // A payment for this order is already being confirmed: wait, don't retry.
    if (error.code === "checkout_payment_confirming") return t("error.paymentConfirming");
    // Another tab changed the session; a fresh attempt resolves it.
    if (error.code === "checkout_session_conflict") return t("error.sessionConflict");
  }
  return t("error.payFailed");
}

// Maps every documented coupon apply/remove error code to a concise, localized
// line. Channel / currency / order-total / not-supported-for-order cases share
// one message (the distinction doesn't help the customer act); found, expired
// and usage-limit-reached stay distinct since the fix differs (try another
// code vs. wait vs. give up). Unknown codes fall back to a generic message.
function couponErrorMessage(t: Translate, error: unknown): string {
  const code = error instanceof CheckoutApiError ? error.code : null;
  switch (code) {
    case "coupon_not_found":
      return t("coupon.error.notFound");
    case "coupon_expired":
      return t("coupon.error.expired");
    case "coupon_revoked":
      return t("coupon.error.revoked");
    case "coupon_not_started":
      return t("coupon.error.notStarted");
    case "coupon_not_applicable_channel":
    case "coupon_currency_mismatch":
    case "coupon_not_applicable_to_total":
    case "coupon_not_supported_for_order":
      return t("coupon.error.notApplicable");
    case "coupon_usage_limit_reached":
      return t("coupon.error.usageLimit");
    case "coupon_already_applied":
      return t("coupon.error.alreadyApplied");
    case "checkout_session_active":
      return t("coupon.error.retry");
    default:
      return t("coupon.error.generic");
  }
}
